// packages/ui/src/sidebar/sidebarActions.ts
import type { PlatformBridge } from '@slate/core';
import { useDocStore } from '../store/docStore';

export interface SidebarFileActions {
  /** Create a new untitled document. */
  onNew: () => void;
  /** Open a file via the platform dialog and add it to the open docs. */
  onOpenFile: () => Promise<void>;
  /** Open a folder via the platform dialog and add it as a workspace root. */
  onOpenFolder: () => Promise<void>;
}

/**
 * The shared New / Open File / Open Folder actions, used by both the ActivityBar
 * rail and the Explorer panel's empty state, so the behavior lives in one place.
 * Store access is read live via `getState()` so the result is stable per
 * platform.
 */
export function makeSidebarFileActions(platform: PlatformBridge): SidebarFileActions {
  return {
    onNew: () => useDocStore.getState().newUntitled(),
    onOpenFile: async () => {
      const opened = await platform.openFileDialog({
        filters: [{ name: 'Markdown', extensions: ['md', 'markdown', 'mdown', 'mkd', 'txt'] }],
      });
      if (opened) useDocStore.getState().openDoc(opened.ref, opened.content);
    },
    onOpenFolder: async () => {
      const ref = await platform.openFolderDialog();
      if (ref) {
        useDocStore.getState().addWorkspaceRoot(ref);
        void platform.pushRecentWorkspace(ref).catch(() => {});
      }
    },
  };
}
